
import React from 'react';

import { Card } from '@/components/ui/card';
import { MacroBeltTimeline, MesoBeltTimeline, MicroBeltTimeline, ZoomableTimeline } from '@/components/roadmap/timeline';
import { TrainingJournalProvider } from '@/features/training-journal/context/TrainingJournalContext';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { MacroCurriculum, MesoCurriculum } from '@/services/roadmap/roadmapService';
// import { useOfflineRoadmapMacro, useOfflineRoadmapMeso } from '@/contexts/offline';

const Roadmap: React.FC = () => {
  const { toast } = useToast();

  // Temporarily disabled offline-first hooks to fix circular dependencies
  // const {
  //   data: macroData,
  //   loading: macroLoading,
  //   error: macroError,
  //   refresh: refreshMacro,
  //   isFromCache: macroFromCache
  // } = useOfflineRoadmapMacro({ cacheFirst: true });

  // const {
  //   data: mesoData,
  //   loading: mesoLoading,
  //   error: mesoError,
  //   refresh: refreshMeso,
  //   isFromCache: mesoFromCache
  // } = useOfflineRoadmapMeso({ cacheFirst: true });

  // Fallback to empty data while offline functionality is disabled
  const macroData: MacroCurriculum[] = [];
  const macroLoading = false;
  const macroError = null;
  const refreshMacro = () => {};
  const macroFromCache = false;

  const mesoData: MesoCurriculum[] = [];
  const mesoLoading = false;
  const mesoError = null;
  const refreshMeso = () => {};
  const mesoFromCache = false;

  const loading = macroLoading || mesoLoading;
  const error = macroError || mesoError;
  const isFromCache = macroFromCache && mesoFromCache;

  const handleRefresh = async () => {
    try {
      await Promise.all([refreshMacro(), refreshMeso()]);
      toast({
        title: 'Success',
        description: 'Roadmap data refreshed successfully.',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to refresh roadmap data. Please try again.',
        variant: 'destructive'
      });
    }
  };
  


  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
          <p className="text-lg text-muted-foreground">
            Loading roadmap data{isFromCache ? ' from cache' : ''}...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto py-8 px-4">
        <div className="p-6 border border-destructive/30 rounded-lg bg-destructive/10 text-center">
          <h2 className="text-xl font-semibold text-destructive mb-2">Error Loading Roadmap</h2>
          <p className="mb-4 text-muted-foreground">{error}</p>
          <Button onClick={handleRefresh} variant="outline">
            Retry Loading
          </Button>
        </div>
      </div>
    );
  }
  
  return (
    <TrainingJournalProvider>
      <div className="container mx-auto py-3 px-2 sm:px-4 md:px-6">
        <div className="flex justify-between items-center mb-4 sm:mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold">BJJ Curriculum Roadmap</h1>
          
        </div>

        {/* Show offline indicator if data is from cache */}
        {isFromCache && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-700">
              📱 Viewing cached data (offline mode). Refresh to get latest updates when online.
            </p>
          </div>
        )}

        {/* Macro Timeline */}
        <div className="mb-6">
          <h2 className="text-xl font-semibold mb-2 px-2">Macro Roadmap</h2>
          <Card className="p-2 sm:p-4 overflow-hidden">
            <div className="overflow-hidden">
              <MacroBeltTimeline data={macroData || []} />
            </div>
          </Card>
        </div>

        {/* Meso Timeline */}
        <div className="mb-6">
          <h2 className="text-xl font-semibold mb-2 px-2">Meso Roadmap</h2>
          <Card className="p-2 sm:p-4 overflow-hidden">
            <div className="overflow-hidden">
              <MesoBeltTimeline data={mesoData || []} />
            </div>
          </Card>
        </div>

        {/* Micro Timeline */}
        <div className="mb-6">
          <h2 className="text-xl font-semibold mb-2 px-2">Micro Roadmap</h2>
          <Card className="p-2 sm:p-4 overflow-hidden">
            <div className="overflow-hidden">
              <MicroBeltTimeline data={mesoData || []} />
            </div>
          </Card>
        </div>

        {/* Zoomable Timeline - New Container */}
        <ZoomableTimeline macroData={macroData || []} mesoData={mesoData || []} />
      </div>
    </TrainingJournalProvider>
  );
};

export default Roadmap;
